import type {SolarProject} from '../../types/solar';
import type {OptimizationEffort} from './SolarApiClient';
import {
    getProjectFromLocalStorage,
    saveProjectToLocalStorage,
    saveCurrentProjectToLocalStorage,
} from '../../utils/LocalStorageManager';
import {getProjectData, getProjectStatus} from './SolarApiClient';
import type {PollStepResult} from './ProjectPoller';
import type {SlimmedProjectGetDataResponse, SolarPlannerStatusResponse} from "@shared/interfaces/solar";
import type {ApiErrorResponse} from "@shared/interfaces/apiError";
import {createLogger} from "../logger.ts";

const logger = createLogger("ProjectManager");

const emptySolarProject = {} as SolarProject;

/**
 * ProjectManager handles the lifecycle of a Solar Project
 * - Loads project data from local storage
 * - Keeps track of ProjectGetDataResponse data
 * - Persists changes to local storage
 * - Handles partial updates as data is filled over time
 */
export class ProjectManager {
    private currentProject = emptySolarProject;
    private isActive: boolean = false;
    private address: AddressWithPrediction | null = null;
    private optimizationEffort: OptimizationEffort = 'fast';

    init(address: AddressWithPrediction, optimizationEffort: OptimizationEffort = 'fast') {
        this.clear();
        this.address = address;
        this.optimizationEffort = optimizationEffort;
        this.activate(this.address, optimizationEffort);
    }

    activate(address: AddressWithPrediction, optimizationEffort: OptimizationEffort = 'fast'): void {
        // A new address or a different optimization mode should re-initialize the manager
        if (this.address === null || address !== this.address || this.optimizationEffort !== optimizationEffort) {
            return this.init(address, optimizationEffort);
        }

        // If already active with the same address and mode, do nothing
        if (this.isActive) {
            return;
        }

        const savedProject = getProjectFromLocalStorage(this.address, this.optimizationEffort);
        if (savedProject) {
            this.currentProject = savedProject;
            logger.debug('ProjectManager activated with project from local storage', { currentProject: this.currentProject });
        }
        this.isActive = true;
    }

    isActivated(): boolean {
        return this.isActive;
    }

    getProject(): SolarProject {
        return this.currentProject;
    }

    save() {
        saveProjectToLocalStorage(this.currentProject!);
        saveCurrentProjectToLocalStorage(this.currentProject!);
        // saveCurrentProjectDataToLocalStorage(this.currentProject!.projectId, this.currentProject!.data as ProjectGetDataResponse);
    }

    setProject(project: SolarProject): void {
        this.currentProject = project;
        this.save();
        logger.debug('ProjectManager set new project', { project });
    }

    /**
     * Set complete project data (ProjectGetDataResponse)
     * Saves full data for current project to localStorage
     * @param data - SlimmedProjectGetDataResponse
     */
    setProjectData(data: SlimmedProjectGetDataResponse | ApiErrorResponse): void {
        if (!this.hasProject()) {
            throw new Error('No project initialized. Call setProject first.');
        }

        this.currentProject.data = data as SlimmedProjectGetDataResponse;

        this.save();
        logger.debug('ProjectManager set complete project data', { data: this.currentProject.data });
    }

    /**
     * Clear current project
     */
    clear(): void {
        this.currentProject = emptySolarProject;
        this.isActive = false;
        this.address = null;
        this.optimizationEffort = 'fast';
        logger.debug('ProjectManager cleared');
    }

    /**
     * Check if manager has an active project
     */
    hasProject(): boolean {
        return this.currentProject !== emptySolarProject;
    }

    getState() {
        return this.currentProject.state;
    }

    async getProjectStatus(): Promise<SolarPlannerStatusResponse | ApiErrorResponse> {
        if (!this.hasProject()) {
            throw new Error('No project initialized. Call setProject first.');
        }

        const status = await getProjectStatus(this.currentProject.projectId);
        if ((status as SolarPlannerStatusResponse).userErrorMessage) {
            logger.error('Error fetching project status', { error: (status as ApiErrorResponse).error });
            this.currentProject.state = 'ERROR';
            this.currentProject.error = (status as SolarPlannerStatusResponse).userErrorMessage || { code: 'UNKNOWN_ERROR', message: 'Unknown error' };
            this.save();

            return status;
        }

        switch (true) {
            case (status as SolarPlannerStatusResponse).hasError:
                this.currentProject.state = 'ERROR';
                break;
            case (status as SolarPlannerStatusResponse).isCompleted && (status as SolarPlannerStatusResponse).optimizerCompleted:
                this.currentProject.state = 'COMPLETED';
                break;
            default:
                this.currentProject.state = 'IN_PROGRESS';
        }

        this.save();
        logger.debug('ProjectManager fetched project status', { status });

        return status;
    }

    isGenerationComplete(): boolean {
        return this.currentProject.state === 'COMPLETED';
    }

    async fetchAndPersistProjectDataIfComplete(): Promise<SlimmedProjectGetDataResponse | ApiErrorResponse | null> {
        if (!this.hasProject()) {
            throw new Error('No project initialized. Call setProject first.');
        }

        await this.getProjectStatus();

        if (this.isGenerationComplete()) {
            logger.debug('Project generation complete, fetching and persisting project data');
            const projectData = await getProjectData(this.currentProject.projectId);
            this.setProjectData(projectData);
            return projectData;
        }

        return null;
    }

    async fetchProjectData(): Promise<boolean> {
        if (!this.hasProject()) {
            throw new Error('No project initialized. Call setProject first.');
        }

        // Check if we already have data in memory
        if (this.currentProject.data) {
            logger.debug('Project data already in memory');
            return true;
        }

        // Fetch from API
        logger.debug('Fetching project data from API for project', { projectId: this.currentProject.projectId });
        const projectData = await getProjectData(this.currentProject.projectId);

        if (projectData) {
            this.currentProject.data = projectData;
            this.save();
            return true;
        }

        return false;
    }

    /**
     * Performs a single project-generation polling tick.
     *
     * Refreshes the upstream status (which maps it onto the project state) and,
     * when the project has completed, fetches and persists the full project data.
     * Returns a transport-agnostic result so a poller can drive the loop without
     * any knowledge of the project domain.
     */
    async pollGenerationStep(): Promise<PollStepResult> {
        await this.getProjectStatus();

        if (this.currentProject.state === 'COMPLETED') {
            const projectData = await getProjectData(this.currentProject.projectId);
            this.setProjectData(projectData);
            return {status: 'COMPLETED'};
        }

        if (this.currentProject.state === 'ERROR') {
            return {
                status: 'ERROR',
                error: this.currentProject.error?.message || 'Unknown error during project generation',
            };
        }

        return {status: 'IN_PROGRESS'};
    }

    /**
     * Marks the current project's generation as failed and persists the change.
     * Used for both polling errors and timeouts (both persist an ERROR state).
     */
    markGenerationError(): void {
        if (!this.hasProject()) {
            return;
        }

        this.currentProject.state = 'ERROR';
        this.save();
    }
}

// Export singleton instance for global use
export const projectManager = new ProjectManager();